/**
 * ChunkErrorBoundary — catches a lazy chunk that failed to load, anywhere under the routes.
 *
 * React.lazy rethrows a rejected import() during render, and with no boundary React unmounts
 * the whole app to a white page. Usually that is a tab left open across a deploy, so this
 * reloads the page once (lib/chunkReload.ts) and shows a Reload prompt until it lands. When
 * the reload guard holds a second reload back, the prompt is what stays on screen.
 *
 * Only failed chunk loads are caught. Every other error is rethrown, so a code bug still fails
 * the way it did before instead of hiding behind "reload". App.tsx mounts it in production
 * builds only: dev never reports a failed chunk load, so there it could only rethrow.
 */

import { Component, type CSSProperties, type ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider.tsx';
import { isChunkLoadError, reloadForChunkError, reloadChunkErrorManually } from '../lib/chunkReload.ts';

export interface ChunkErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ChunkErrorBoundaryState {
  readonly failed: boolean;
  readonly error: unknown;
}

const SHELL_STYLE: CSSProperties = {
  minHeight: '60vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: 24,
  textAlign: 'center',
  color: 'var(--text-secondary)',
};

function ReloadPrompt(): JSX.Element {
  const { t } = useI18n();
  return (
    <div role="alert" style={SHELL_STYLE}>
      <p>{t('chrome.chunkError.body')}</p>
      <button type="button" className="jub-btn-primary" onClick={() => reloadChunkErrorManually()}>
        {t('chrome.chunkError.reload')}
      </button>
    </div>
  );
}

export class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  state: ChunkErrorBoundaryState = { failed: false, error: null };

  static getDerivedStateFromError(error: unknown): ChunkErrorBoundaryState {
    return { failed: true, error };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error)) reloadForChunkError();
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    if (!isChunkLoadError(this.state.error)) throw this.state.error;
    return <ReloadPrompt />;
  }
}
